import * as React from 'react'
import { Box, Form } from 'cs-kevlar-react'
import { Formik } from 'formik'
import {
  EditModalActionBar,
  FormikTextField,
  FormikSelect,
  ConfirmDeleteModal,
} from 'components'
import { Grid } from 'cs-kevlar-react'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Guid, LicensedAgencyViewModel, Agency } from '../../api'
import {
  useCustomerApi,
  usePageNotifications,
  useCurrentCustomer,
  useLogger,
} from 'hooks'
import { LicensedAgency } from 'contexts/CurrentCustomerContext'
import * as Yup from 'yup'
import { getAllAdvancedAuthenticationTypes } from 'utils'
import { Option } from 'cs-kevlar-react/lib/es/Select'

interface EditAgencyProps {
  agency: LicensedAgency
  onClose: () => void
  agencyDocuments: Agency[] | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      padding: theme.spacing(10),
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      marginLeft: -2 * Spacing,
      marginRight: -2 * Spacing,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    gridItem: {
      width: '100%',
    },
  }),
)

const AdvancedAuthenticationTypes = getAllAdvancedAuthenticationTypes()

export const EditAgency: React.FunctionComponent<EditAgencyProps> = ({
  agency,
  onClose,
  agencyDocuments,
}) => {
  const classes = useStyles({})
  const { logError } = useLogger()

  const { agencies, dispatch: dispatchCustomerAction } = useCurrentCustomer(
    agency.customerAgencyId,
  )

  const {
    editLicensedAgency,
    deleteLicensedAgency,
    addLicensedAgency,
  } = useCustomerApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(
    false,
  )

  const agencyNameValidator = React.useMemo(() => {
    const agencyIds = new Set<string>()
    // ignore the current agency ID (uniqueidentifer) since we need to be able to save it with the current ID
    agencies
      .filter((item) => item.id !== agency.id)
      .map((item) => agencyIds.add(item.uniqueIdentifier))

    return (uniqueIdentifier: string) => !agencyIds.has(uniqueIdentifier)
  }, [agencies, agency.uniqueIdentifier])

  const EditAgencyFormSchema = Yup.object().shape({
    documentId: Yup.string().required('The Agency field is required.'),
    uniqueIdentifier: Yup.string()
      .test(
        'Name is unique',
        'Agency ID field must be unique',
        agencyNameValidator,
      )
      .required('The Agency ID field is required.'),
    licenseCount: Yup.number().required('The Licenses field is required.'),
    advancedAuthType: Yup.number().required(
      'The Authentication Type field is required.',
    ),
    notes: Yup.string(),
  })

  const isAdding = agency.id === 'NEW'
  const isEditing = agency.id !== 'NEW'

  const onSubmit = (values: LicensedAgency, formikActions: any) => {
    // we need to grab the name for the selected agency document and toss that in the values collection
    const selectedAgency = agencyDocuments?.filter(
      (agency) => agency.id === values.documentId,
    )[0] as Agency

    values.name = selectedAgency.name

    if (isEditing) onEdit(agency.id, values, onClose, formikActions)
    if (isAdding) onAdd(values, onClose, formikActions)
  }

  const onEdit = (
    customerAgencyId: Guid,
    values: LicensedAgency,
    onComplete: () => void,
    formikActions: any,
  ) => {
    editLicensedAgency(customerAgencyId, values.id, values)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const agencyModel = {
            ...response.data.data,
            id: response.data.data.licensedAgencyId,
          }

          dispatchCustomerAction({
            type: 'EditLicensedAgency',
            payload: agencyModel,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Agency updated successfully.',
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error editing the agency',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error editing the agency',
        })
      })
  }

  const onAdd = (
    values: LicensedAgency,
    onComplete: () => void,
    formikActions: any,
  ) => {
    // remove the NEW id and licensedAgencyId so that the server will generate a new GUID
    const newAgency = Object.keys(values)
      .filter((key) => key !== 'id' && key !== 'licensedAgencyId')
      .reduce((accum, current) => {
        const value = (values as any)[current]
        return {
          ...accum,
          [current]: value,
        } as LicensedAgencyViewModel
      }, {} as LicensedAgencyViewModel)

    addLicensedAgency(agency.customerAgencyId, newAgency)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const agencyModel = {
            ...response.data.data,
            id: response.data.data.licensedAgencyId,
          }

          dispatchCustomerAction({
            type: 'AddLicensedAgency',
            payload: agencyModel,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Agency added successfully.',
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: response.data.errorMessage
              ? 'Could not add agency: ' + response.data.errorMessage
              : 'There was an error adding the agency',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error adding the agency',
        })
      })
  }

  const onDelete = () => {
    deleteLicensedAgency(agency.customerAgencyId, agency.id)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          dispatchCustomerAction({
            type: 'DeleteLicensedAgency',
            payload: agency.id,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Agency deleted successfully.',
          })

          setIsConfirmingDelete(false)
          onClose()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error deleting the agency',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error deleting the agency',
        })
      })
  }

  const agencyWithDefaults = {
    ...agency,
  }

  if (agencyWithDefaults.notes === null) {
    agencyWithDefaults.notes = ''
  }

  const agencyOptions: Option[] = agencyDocuments
    ? agencyDocuments.map((agency: Agency) => ({
        key: agency.id,
        value: agency.id,
        label: agency.name + ' (ID: ' + agency.externalId + ')',
      }))
    : []

  return (
    <React.Fragment>
      <Formik
        initialValues={agencyWithDefaults}
        validationSchema={EditAgencyFormSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Box className={classes.formFieldContainer}>
              <Grid
                container
                spacing={Spacing}
                direction="column"
                alignItems="flex-start"
                justifyContent="center"
                className={classes.formFields}
              >
                <Grid item className={classes.gridItem}>
                  <FormikSelect
                    id="EditAgency::Agency"
                    formikProps={formikProps}
                    label="Name"
                    fieldName="documentId"
                    options={agencyOptions}
                    fullWidth
                    readOnly={isEditing}
                    required
                  />
                </Grid>
                <Grid item className={classes.gridItem}>
                  <FormikTextField
                    formikProps={formikProps}
                    fieldName="uniqueIdentifier"
                    label="Agency ID"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item className={classes.gridItem}>
                  <FormikTextField
                    formikProps={formikProps}
                    fieldName="licenseCount"
                    label="Licenses"
                    type="number"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item className={classes.gridItem}>
                  <FormikSelect
                    id="EditAgency::AuthenticationType"
                    formikProps={formikProps}
                    label="Authentication Type"
                    fieldName="advancedAuthType"
                    fullWidth
                    options={AdvancedAuthenticationTypes}
                    required
                  />
                </Grid>
                <Grid item className={classes.gridItem}>
                  <FormikTextField
                    formikProps={formikProps}
                    fieldName="notes"
                    label="Notes"
                    fullWidth
                    multiline
                    rows={5}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.spacer} />
            <EditModalActionBar
              canDelete={isEditing}
              onDelete={() => setIsConfirmingDelete(true)}
              onCancel={onClose}
              formikProps={formikProps}
              saveText={isAdding ? 'Add' : 'Save'}
              deleteText="Delete Agency"
            />
          </Form>
        )}
      </Formik>
      {isEditing && (
        <ConfirmDeleteModal
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={onDelete}
          open={isConfirmingDelete}
          line1Text="Please confirm you wish to delete this agency.  This action cannot be reversed."
          line2Text={`Agency: ${agency.name}`}
          inputLabelText="Please enter DELETE to confirm deletion of this agency."
        />
      )}
    </React.Fragment>
  );
}
